import type { StateCreator } from 'zustand';
import type { VoyagerState } from '../useVoyagerStore';
import type {
  WorkspaceAgentTurn,
  WorkspaceAgentResponse,
  WorkspaceAgentPatch,
  WorkspaceAgentFilter,
} from '../../types';
import { generateId } from '../../utils';
import { authHeaders } from '../../auth/clerk';

const PANEL_WIDTH_KEY = 'voyager:workspaceAgentPanelWidth';
const DEFAULT_PANEL_WIDTH = 420;
const MIN_PANEL_WIDTH = 320;
const MAX_PANEL_WIDTH = 720;

function readPanelWidth(): number {
  try {
    const v = localStorage.getItem(PANEL_WIDTH_KEY);
    const n = v ? Number(v) : NaN;
    if (Number.isFinite(n) && n >= MIN_PANEL_WIDTH && n <= MAX_PANEL_WIDTH) return n;
  } catch {}
  return DEFAULT_PANEL_WIDTH;
}

export interface WorkspaceAgentSlice {
  workspaceAgentPanelOpen: boolean;
  workspaceAgentPanelWidth: number;
  workspaceAgentTurns: WorkspaceAgentTurn[];
  workspaceAgentStatus: 'idle' | 'running' | 'error';
  workspaceAgentError: string | null;
  workspaceAgentThreadId: string | null;

  toggleWorkspaceAgentPanel: () => void;
  setWorkspaceAgentPanelWidth: (w: number) => void;
  sendWorkspaceAgentQuery: (text: string) => Promise<void>;
  applyWorkspaceAgentPatch: (turnId: string) => Promise<void>;
  dismissWorkspaceAgentPatch: (turnId: string) => void;
  markWorkspaceAgentClarificationSubmitted: (turnId: string) => void;
  clearWorkspaceAgentHistory: () => void;
}

export const createWorkspaceAgentSlice: StateCreator<
  VoyagerState, [], [], WorkspaceAgentSlice
> = (set, get) => ({
  workspaceAgentPanelOpen: false,
  workspaceAgentPanelWidth: readPanelWidth(),
  workspaceAgentTurns: [],
  workspaceAgentStatus: 'idle',
  workspaceAgentError: null,
  workspaceAgentThreadId: null,

  toggleWorkspaceAgentPanel: () => {
    set({ workspaceAgentPanelOpen: !get().workspaceAgentPanelOpen });
  },

  setWorkspaceAgentPanelWidth: (w: number) => {
    const clamped = Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, Math.round(w)));
    try { localStorage.setItem(PANEL_WIDTH_KEY, String(clamped)); } catch {}
    set({ workspaceAgentPanelWidth: clamped });
  },

  sendWorkspaceAgentQuery: async (text: string) => {
    const q = (text || '').trim();
    if (!q) return;

    const state = get();
    const perspectiveId = state.selectedPerspective;
    if (!perspectiveId) {
      set({ workspaceAgentError: 'Pick a perspective first.' });
      return;
    }

    let threadId = state.workspaceAgentThreadId;
    if (!threadId) {
      threadId = `wsa_thr_${generateId()}`;
      set({ workspaceAgentThreadId: threadId });
    }
    const turnId = `wsa_${Date.now()}`;
    const newTurn: WorkspaceAgentTurn = {
      id: turnId,
      userText: q,
      sentAt: Date.now(),
      status: 'running',
      response: null,
      applied: false,
      dismissed: false,
      clarificationSubmitted: false,
      error: null,
    };

    const prevTurns = state.workspaceAgentTurns;
    set({
      workspaceAgentTurns: [...prevTurns, newTurn],
      workspaceAgentStatus: 'running',
      workspaceAgentError: null,
    });

    const filters: WorkspaceAgentFilter[] = (state.filters || []).map((f) => ({
      column: f.column,
      operator: f.operator,
      value: f.value,
    }));

    const history = prevTurns.slice(-6).flatMap((t) => {
      const lines: { role: 'user' | 'assistant'; content: string }[] = [
        { role: 'user', content: t.userText },
      ];
      const expl = t.response?.explanation;
      if (expl) lines.push({ role: 'assistant', content: expl });
      return lines;
    });

    const qr = state.queryResults;
    const queryResultsBlock = qr && qr.rows && qr.rows.length > 0
      ? {
          columns: qr.columns,
          // Send all rows — the backend's prepare_for_formatter will decide
          // between full-rows and aggregated-summary based on size.
          rows: qr.rows,
          row_count: qr.row_count,
          executed_sql: state.querySQL || null,
        }
      : null;

    const body = {
      query: q,
      thread_id: threadId,
      context: {
        domain_id: state.selectedDomain,
        perspective_id: perspectiveId,
        selected_metrics: state.selectedMetrics.map((m) => m.metricName),
        selected_dimensions: [...state.selectedDimensions],
        filters,
        active_date_field: state.activeDateField,
        date_range_preset: state.dateRangePreset,
        date_range_start: state.dateRangeStart,
        date_range_end: state.dateRangeEnd,
        query_results: queryResultsBlock,
      },
      history,
    };

    try {
      const base = process.env.REACT_APP_API_BASE || '';
      const resp = await fetch(`${base}/api/workspace-agent/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const detail = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${detail}`);
      }
      const data = (await resp.json()) as WorkspaceAgentResponse;

      const turns = get().workspaceAgentTurns;
      const idx = turns.findIndex((t) => t.id === turnId);
      if (idx < 0) return;
      const merged: WorkspaceAgentTurn = {
        ...turns[idx],
        status: data.kind === 'error' ? 'error' : 'done',
        response: data,
        error: data.kind === 'error' ? data.error || 'Agent returned an error' : null,
      };
      set({
        workspaceAgentTurns: turns.map((t, i) => (i === idx ? merged : t)),
        workspaceAgentStatus: data.kind === 'error' ? 'error' : 'idle',
        workspaceAgentError: data.kind === 'error' ? data.error || null : null,
      });
    } catch (e: any) {
      const turns = get().workspaceAgentTurns;
      const idx = turns.findIndex((t) => t.id === turnId);
      if (idx >= 0) {
        const merged: WorkspaceAgentTurn = {
          ...turns[idx],
          status: 'error',
          error: e?.message || 'Request failed',
        };
        set({ workspaceAgentTurns: turns.map((t, i) => (i === idx ? merged : t)) });
      }
      set({ workspaceAgentStatus: 'error', workspaceAgentError: e?.message || 'Request failed' });
    }
  },

  applyWorkspaceAgentPatch: async (turnId: string) => {
    const turns = get().workspaceAgentTurns;
    const turn = turns.find((t) => t.id === turnId);
    if (!turn) return;
    const patch: WorkspaceAgentPatch | null | undefined = turn.response?.patch;
    if (!patch) return;

    await get().applyAgentPatchToWorkspace(patch);

    const updated = get().workspaceAgentTurns.map((t) =>
      t.id === turnId ? { ...t, applied: true } : t,
    );
    set({ workspaceAgentTurns: updated });
  },

  dismissWorkspaceAgentPatch: (turnId: string) => {
    const updated = get().workspaceAgentTurns.map((t) =>
      t.id === turnId ? { ...t, dismissed: true } : t,
    );
    set({ workspaceAgentTurns: updated });
  },

  markWorkspaceAgentClarificationSubmitted: (turnId: string) => {
    const updated = get().workspaceAgentTurns.map((t) =>
      t.id === turnId ? { ...t, clarificationSubmitted: true } : t,
    );
    set({ workspaceAgentTurns: updated });
  },

  clearWorkspaceAgentHistory: () => {
    set({
      workspaceAgentTurns: [],
      workspaceAgentStatus: 'idle',
      workspaceAgentError: null,
      workspaceAgentThreadId: null,
    });
  },
});
